import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { outpostStyles, FadingVideo, BlurText, OutpostNavbar } from '../components/OutpostShared';

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
      >
        <span className="font-heading italic text-2xl md:text-3xl text-black pr-8 group-hover:text-black/70 transition-colors">{q}</span>
        <span className="text-black/40 flex-shrink-0 bg-black/5 rounded-full p-2 group-hover:bg-black/10 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <p className="pb-8 text-black/70 font-body text-lg leading-relaxed max-w-3xl pt-2">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function EcommerceOptimization() {
  const items = [
    "A+ Content Creation", "Amazon Advertising", "Listing Optimization", 
    "Search Engine Optimization", "Brand Registry", "Competitive Analysis", "Launch Strategies"
  ];

  const problemStats = [
    { stat: "9.7M", text: "Global sellers compete on Amazon. Fewer than 3% consistently rank on page one." },
    { stat: "Page 1", text: "The average Amazon shopper never scrolls past the first page of search results." },
    { stat: "30-50%", text: "Potential revenue lost to competitors by brands without optimized listings." },
    { stat: "70%", text: "Of Amazon searches are unbranded. Customers are looking for products, not names." }
  ];

  const processSteps = [
    { step: "01", title: "Audit", desc: "We analyze your current listings, ads, competitors, and market position." },
    { step: "02", title: "Strategize", desc: "We build a custom roadmap based on your goals, budget, and timeline." },
    { step: "03", title: "Optimize", desc: "We implement listing, content, and advertising improvements." },
    { step: "04", title: "Launch", desc: "We execute launch or relaunch strategies for maximum velocity." },
    { step: "05", title: "Scale", desc: "We continuously test, refine, and expand for compounding growth." }
  ];

  const metrics = [
    { title: "A+ Content", value: "3–10%", desc: "Average increase in sales conversion rates." },
    { title: "Optimized Listings", value: "200%+", desc: "Improvement in organic ranking via keyword integration." },
    { title: "Amazon Ads", value: "4–7x", desc: "Average ROAS for well-managed campaigns." },
    { title: "Brand Registry", value: "99%", desc: "Reduction in hijacker incidents and counterfeits." }
  ];

  const faqs = [
    { q: "Do you work with brands that are new to Amazon?", a: "Absolutely. Whether you're launching your first product or optimizing an existing catalog, we tailor our approach to your stage of growth." },
    { q: "What Amazon ad types do you manage?", a: "We manage Sponsored Products, Sponsored Brands, Sponsored Display, and DSP campaigns depending on your goals and budget." },
    { q: "How quickly will I see results?", a: "Listing optimizations often show impact within 2–4 weeks. Advertising and SEO improvements typically compound over 30–90 days." },
    { q: "Do you only work with Amazon, or other marketplaces too?", a: "While Amazon is our specialty, our e-commerce strategies extend to Walmart Marketplace, Shopify, and other DTC platforms." },
    { q: "What does pricing look like?", a: "We offer flexible engagement models—monthly retainers, project-based work, and performance-tied structures. Every engagement starts with an initial marketplace audit." }
  ];

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* SECTION 1: Hero */}
      <section className="relative min-h-screen w-full flex flex-col items-center overflow-hidden pt-32 pb-24 px-8 md:px-16 lg:px-20">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_094631_d30ab262-45ee-4b7d-99f3-5d5848c8ef13.mp4"
          className="absolute inset-0 w-full h-full object-cover z-0"
          maxOpacity={0.15}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 my-auto">
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest"
            >
              // Expertise 03
            </motion.div>
            
            <BlurText 
              text="E-Commerce & Amazon" 
              justify="flex-start"
              className="text-6xl md:text-7xl lg:text-[5.5rem] font-heading italic text-black leading-[0.85] tracking-[-3px] mb-8 text-left"
            />
            
            <motion.p 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-black/80 font-body font-light leading-relaxed max-w-xl mb-10"
            >
              Dominate the world's largest marketplace with a strategy built for visibility, conversion, and enduring growth. We optimize every touchpoint—from A+ Content to advertising and SEO—so your brand stands out, sells more, and scales faster.
            </motion.p>

            <div className="flex flex-col gap-4 mb-10">
              <h4 className="font-heading italic text-2xl text-black mb-4">Core Capabilities</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                {items.map((item, i) => (
                  <motion.div 
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (i * 0.1) }}
                    className="flex items-center gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-black/40 shrink-0"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-black/80">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <Link to="/contact" className="bg-black text-white rounded-full px-7 py-3.5 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors w-fit shadow-lg shadow-black/10">
                Get Your Marketplace Audit
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Link>
            </motion.div>
          </div>

          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6 relative items-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-white/20 blur-3xl rounded-full -z-10"></div>
            
            <div className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5 sm:mt-12">
              <span className="text-black/40 font-body text-[10px] tracking-widest uppercase">Video/Image Slot 1</span>
            </div>
            
            <div className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden liquid-glass flex flex-col items-center justify-center border border-black/5">
              <span className="text-black/40 font-body text-[10px] tracking-widest uppercase">Video/Image Slot 2</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Problem Statement */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 max-w-2xl"
          >
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Why Most Brands Struggle on Amazon</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed">
              The marketplace rewards strategy, not luck. Without optimization, you're leaving money on the table for competitors to claim.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemStats.map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 flex flex-col border border-black/5 hover:border-black/10 transition-colors"
              >
                <span className="font-heading italic text-5xl text-black mb-4">{stat.stat}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{stat.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Process */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Our Marketplace Process</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">A systematic approach to scaling your catalog and maximizing market share.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {processSteps.map((s, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-6 flex flex-col shadow-sm"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="text-sm font-body uppercase tracking-widest text-black/40">Step</span>
                  <span className="text-3xl font-heading italic text-black/30">{s.step}</span>
                </div>
                <h4 className="font-body font-semibold text-black text-lg mb-3">{s.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Numbers */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/3">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">The Numbers That Matter</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed mb-6">
              As we grow together, these industry benchmarks become your benchmarks—tracked, reported, and continuously improved.
            </p>
          </motion.div>
          
          <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {metrics.map((metric, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 flex flex-col shadow-sm"
              >
                <h4 className="font-body text-sm uppercase tracking-widest text-black/50 mb-2">{metric.title}</h4>
                <span className="font-heading italic text-5xl text-black mb-4">{metric.value}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: FAQ */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black">Common Questions</h2>
          </motion.div>

          <div className="flex flex-col">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <FAQItem q={faq.q} a={faq.a} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: Final CTA */}
      <section className="relative w-full pb-32 px-8 md:px-16 lg:px-20 flex justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="liquid-glass-strong rounded-[2.5rem] p-12 md:p-24 w-full max-w-6xl text-center flex flex-col items-center border border-black/10 shadow-2xl shadow-black/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none"></div>
          
          <h2 className="font-heading italic text-5xl md:text-7xl text-black mb-8 relative z-10">Ready to Own Your Market?</h2>
          <p className="text-black/70 font-body max-w-2xl text-lg md:text-xl mb-12 relative z-10">
            Most brands leave money on the table every single day their listings sit unoptimized. Let's fix that. Book your Amazon audit today and uncover your biggest growth opportunities.
          </p>
          <Link to="/contact" className="bg-black text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors shadow-xl shadow-black/20 relative z-10 text-lg">
            Get My Audit
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
        </motion.div>
      </section>

    </div>
  );
}